import { useIsMutating } from '@tanstack/react-query';
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useRef,
  useState,
} from 'react';
import { CompleteOrderError } from 'typescript/constants/enums/checkout';
import {
  PaymentSchedule,
  PaymentType,
} from 'typescript/constants/enums/payment';
import { MUTATION_KEYS } from 'typescript/constants/react_query';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';

export type PendingCompleteOrder = {
  paymentType: PaymentType;
  paymentSchedule?: PaymentSchedule;
  paymentPostalCode: string;
  useSavedPaymentSource: boolean;
  makeDefaultCard: boolean;
  financingDisclosureAcknowledged: boolean;
};

interface ICompleteOrderContext {
  isCompletingOrder: boolean;
  showRecaptcha: boolean;
  setShowRecaptcha: Dispatch<SetStateAction<boolean>>;
  hasPendingCompleteOrder: boolean;
  setPendingCompleteOrder: (
    pendingCompleteOrder: PendingCompleteOrder | null,
  ) => void;
  takePendingCompleteOrder: () => PendingCompleteOrder | null;
  completeOrderError: CompleteOrderError | null;
  setCompleteOrderError: Dispatch<SetStateAction<CompleteOrderError | null>>;
  showCompletingOrderDialog: boolean;
  setShowCompletingOrderDialog: Dispatch<SetStateAction<boolean>>;
  showAlternateCompletingCopy: boolean;
  setShowAlternateCompletingCopy: Dispatch<SetStateAction<boolean>>;
}

const CompleteOrderContext = createContext<ICompleteOrderContext | undefined>(
  undefined,
);

const useCompleteOrderContext = () => {
  const context = useContext(CompleteOrderContext);
  if (context === undefined) {
    throw new Error(
      'useCompleteOrderContext must be used within a CompleteOrderProvider',
    );
  }
  return context;
};

interface CompleteOrderProviderProps {
  children: ReactNode;
}

const CompleteOrderProvider = ({ children }: CompleteOrderProviderProps) => {
  const { orderNumber } = useCheckoutFlow();
  const isCompletingOrder =
    useIsMutating({
      mutationKey: [MUTATION_KEYS.ORDER_COMPLETE, { orderNumber }],
    }) > 0;

  const [completeOrderError, setCompleteOrderError] =
    useState<CompleteOrderError>(null);

  const [showRecaptcha, setShowRecaptcha] = useState(false);
  const pendingCompleteOrderRef = useRef<PendingCompleteOrder | null>(null);
  const [hasPendingCompleteOrder, setHasPendingCompleteOrder] = useState(false);

  const setPendingCompleteOrder = (
    pendingCompleteOrder: PendingCompleteOrder | null,
  ) => {
    pendingCompleteOrderRef.current = pendingCompleteOrder;
    setHasPendingCompleteOrder(Boolean(pendingCompleteOrder));
  };

  const takePendingCompleteOrder = () => {
    const pendingCompleteOrder = pendingCompleteOrderRef.current;
    setPendingCompleteOrder(null);
    return pendingCompleteOrder;
  };

  const [showCompletingOrderDialog, setShowCompletingOrderDialog] =
    useState(false);
  const [showAlternateCompletingCopy, setShowAlternateCompletingCopy] =
    useState(false);

  return (
    <CompleteOrderContext.Provider
      value={{
        isCompletingOrder,
        showRecaptcha,
        setShowRecaptcha,
        hasPendingCompleteOrder,
        setPendingCompleteOrder,
        takePendingCompleteOrder,
        completeOrderError,
        setCompleteOrderError,
        showCompletingOrderDialog,
        setShowCompletingOrderDialog,
        showAlternateCompletingCopy,
        setShowAlternateCompletingCopy,
      }}
    >
      {children}
    </CompleteOrderContext.Provider>
  );
};

export { CompleteOrderProvider, useCompleteOrderContext };
